import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, Trash2, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { logError } from "@/lib/log";
import { PageHeader } from "@/components/kit";
import { HeartBack } from "@/components/HeartBack";

const COLORS = ["#FDE68A", "#FBCFE8", "#C7D2FE", "#BBF7D0", "#FED7AA", "#DDD6FE"];
const EMOJIS = ["☀️", "💜", "🌸", "☕", "🐾", "🎶", "🌿", "😊"];
const shiftMonth = (m, d) => { const [y, mo] = m.split("-").map(Number); const t = new Date(y, mo - 1 + d, 1); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`; };

const Jar = ({ colors, shaking }) => (
  <svg viewBox="0 0 200 240" className={`w-48 h-56 mx-auto ${shaking ? "mm-jar-shake" : ""}`} role="img" aria-label={`Gratitude jar with ${colors.length} notes`}>
    <defs><radialGradient id="jar-glow" cx="50%" cy="60%" r="60%"><stop offset="0" stopColor="#FFF6D6" stopOpacity="0.9" /><stop offset="1" stopColor="#FFF6D6" stopOpacity="0" /></radialGradient></defs>
    <ellipse cx="100" cy="150" rx="95" ry="90" fill="url(#jar-glow)" />
    <rect x="62" y="18" width="76" height="20" rx="6" fill="#B9A3E8" />
    <path d="M58 40 h84 q10 0 10 14 v6 q22 16 22 50 v88 q0 26 -26 26 h-96 q-26 0 -26 -26 v-88 q0 -34 22 -50 v-6 q0 -14 10 -14z" fill="#EEF4FF" fillOpacity="0.55" stroke="#C9D3EE" strokeWidth="3" />
    {colors.slice(0, 48).map((c, i) => {
      const slot = `slot-${i}`;
      const row = Math.floor(i / 6), col = i % 6;
      return <rect key={slot} x={44 + col * 19 + (row % 2) * 6} y={206 - row * 15} width="17" height="12" rx="2" fill={c} stroke="#00000014" transform={`rotate(${((i * 37) % 30) - 15} ${52 + col * 19} ${212 - row * 15})`} />;
    })}
    <path d="M52 80 q-6 40 0 110" stroke="#fff" strokeWidth="6" strokeLinecap="round" opacity="0.6" fill="none" />
  </svg>
);

const AddNote = ({ onAdded }) => {
  const [text, setText] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [emoji, setEmoji] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/gratitude", { text, color, emoji }); setText(""); setEmoji(null); toast.success("Dropped into your jar ✨"); onAdded(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mm-card p-5" data-testid="gratitude-add">
      <label className="font-semibold" htmlFor="grat-text">A small good moment</label>
      <textarea id="grat-text" className="mm-input mt-2 min-h-[80px]" maxLength={280} value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. The sun on my face on the walk home" data-testid="gratitude-text" />
      <div className="flex flex-wrap items-center gap-2 mt-3">
        {COLORS.map((c) => <button type="button" key={c} onClick={() => setColor(c)} className={`w-7 h-7 rounded-full border-2 ${color === c ? "border-mm-primary scale-110" : "border-white"} transition-transform`} style={{ background: c }} aria-label={`Colour ${c}`} data-testid={`gratitude-color-${c.slice(1)}`} />)}
        <span className="w-px h-6 bg-mm-primary/20 mx-1" />
        {EMOJIS.map((e) => <button type="button" key={e} onClick={() => setEmoji(emoji === e ? null : e)} className={`text-xl rounded-full w-9 h-9 ${emoji === e ? "bg-mm-primary/15" : ""}`} aria-label={`Emoji ${e}`} data-testid={`gratitude-emoji-${EMOJIS.indexOf(e)}`}>{e}</button>)}
      </div>
      <div className="flex justify-between items-center mt-3"><span className="text-xs text-mm-muted">{text.length}/280</span>
        <button className="mm-btn" disabled={busy || !text.trim()} data-testid="gratitude-add-btn">Add to my jar</button></div>
    </form>
  );
};

const Unfolded = ({ note, onClose, onAgain, onHeart }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose} data-testid="gratitude-unfolded">
    <div className="mm-envelope-open rounded-3xl p-8 max-w-sm w-full shadow-xl relative" style={{ background: note.color }} onClick={(e) => e.stopPropagation()}>
      <button onClick={onClose} className="absolute top-3 right-3 p-1 text-[#3A2E4F]/60" aria-label="Close" data-testid="gratitude-unfolded-close"><X size={18} /></button>
      {note.emoji && <div className="text-4xl">{note.emoji}</div>}
      {note.memory && <p className="text-sm font-bold text-[#B23A63] mt-2" data-testid="gratitude-memory-lane">🕰️ On this day {note.memory.years_ago === 1 ? "last year" : `${note.memory.years_ago} years ago`}…</p>}
      <p className="text-lg text-[#3A2E4F] mt-3 break-words [overflow-wrap:anywhere]" style={{ fontFamily: "Georgia, serif" }} data-testid="gratitude-unfolded-text">{note.text}</p>
      {note.from_checkin && <p className="text-sm font-semibold text-[#5B3F8F] mt-3" data-testid="gratitude-unfolded-checkin">From your check-in</p>}
      {note.from_name && <div className="flex items-center gap-2 mt-3"><p className="text-sm font-semibold text-[#5B3F8F]" data-testid="gratitude-unfolded-from">From {note.from_name}</p><HeartBack key={`${note.id}-${note.hearted}`} id={note.id} hearted={note.hearted} name={note.from_name} onDone={onHeart} testId="gratitude-unfolded-heart" /></div>}
      <p className="text-xs text-[#3A2E4F]/60 mt-4">{new Date(note.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</p>
      <button onClick={onAgain} className="mt-5 text-sm font-semibold text-[#5B3F8F]" data-testid="gratitude-shake-again">Shake again</button>
    </div>
  </div>
);

export default function GratitudeJar() {
  const [month, setMonth] = useState(null);
  const [current, setCurrent] = useState(null);
  const [data, setData] = useState(null);
  const [note, setNote] = useState(null);
  const [shaking, setShaking] = useState(false);
  const last = useRef(0);

  const load = useCallback(() => api.get("/gratitude", { params: month ? { month } : {} }).then((r) => { setData(r.data); setCurrent((c) => c || (month ? null : r.data.month)); }).catch((e) => toast.error(errMsg(e))), [month]);
  useEffect(() => { load(); }, [load]);

  const shake = useCallback(async () => {
    setShaking(true); navigator.vibrate?.(80);
    try {
      const [{ data: d }] = await Promise.all([api.get("/gratitude/random", { params: note ? { exclude: note.id } : {} }), new Promise((r) => setTimeout(r, 650))]);
      if (d.note) setNote(d.note);
      else toast.message(note ? "That's the only note in your jar so far 💜" : "Your jar is empty. Add a good moment first 💜");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setShaking(false); }
  }, [note]);

  useEffect(() => {
    const onMotion = (e) => {
      const a = e.accelerationIncludingGravity; if (!a) return;
      if (Math.abs(a.x) + Math.abs(a.y) + Math.abs(a.z) > 35 && Date.now() - last.current > 2500) { last.current = Date.now(); shake(); }
    };
    window.addEventListener("devicemotion", onMotion);
    return () => window.removeEventListener("devicemotion", onMotion);
  }, [shake]);

  const tapShake = async () => { try { await window.DeviceMotionEvent?.requestPermission?.(); } catch (e) { logError("Motion permission unavailable, tap still works:", e); } shake(); };
  const del = async (id) => { try { await api.delete(`/gratitude/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-mm-primary" /></div>;
  return (
    <div className="max-w-3xl space-y-6" data-testid="gratitude-page">
      <PageHeader back title="Gratitude Jar" subtitle="Collect small good moments. On hard days, shake the jar to find one." />
      <div className="mm-card p-6 text-center">
        <Jar colors={data.recent_colors} shaking={shaking} />
        <p className="font-semibold mt-2" data-testid="gratitude-total">{data.total} good moment{data.total === 1 ? "" : "s"} in your jar</p>
        <button className="mm-btn mt-3" onClick={tapShake} disabled={shaking || !data.total} data-testid="gratitude-shake-btn">{shaking ? <Loader2 size={16} className="animate-spin" /> : "🫙"}Shake the jar</button>
        <p className="text-xs text-mm-muted mt-2">On a phone you can also shake your device.</p>
      </div>
      <AddNote onAdded={load} />
      <section data-testid="gratitude-month">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">{data.label}</h2>
          <div className="flex gap-1">
            <button className="mm-btn-ghost !px-2" onClick={() => setMonth(shiftMonth(data.month, -1))} aria-label="Previous month" data-testid="gratitude-prev-month"><ChevronLeft size={18} /></button>
            <button className="mm-btn-ghost !px-2" onClick={() => setMonth(shiftMonth(data.month, 1))} disabled={!month || month === current} aria-label="Next month" data-testid="gratitude-next-month"><ChevronRight size={18} /></button>
          </div>
        </div>
        {data.notes.length ? (
          <div className="grid sm:grid-cols-2 gap-3">
            {data.notes.map((n) => (
              <div key={n.id} className="rounded-2xl p-4 flex gap-2" style={{ background: n.color }} data-testid={`gratitude-note-${n.id}`}>
                <div className="flex-1 min-w-0 text-[#3A2E4F]"><p className="break-words [overflow-wrap:anywhere]">{n.emoji} {n.text}</p><p className="text-xs opacity-60 mt-1">{n.from_name && <span className="font-semibold" data-testid={`gratitude-from-${n.id}`}>From {n.from_name} · </span>}{n.from_checkin && <span className="font-semibold" data-testid={`gratitude-checkin-${n.id}`}>From your check-in · </span>}{new Date(n.created_at).toLocaleDateString()}</p>
                  {n.from_name && <div className="mt-2"><HeartBack key={`${n.id}-${n.hearted}`} id={n.id} hearted={n.hearted} name={n.from_name} onDone={load} testId={`gratitude-heart-${n.id}`} /></div>}</div>
                <button onClick={() => del(n.id)} className="text-[#3A2E4F]/50 hover:text-mm-error self-start" aria-label="Delete note" data-testid={`gratitude-delete-${n.id}`}><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-mm-muted" data-testid="gratitude-month-empty">No notes this month yet.</p>}
      </section>
      {note && <Unfolded note={note} onClose={() => setNote(null)} onAgain={shake} onHeart={() => { setNote((n) => ({ ...n, hearted: true })); load(); }} />}
    </div>
  );
}
